/* ============================================================
   useIdentity — the reviewer's self-asserted identity

   One identity per browser, stored in localStorage:
     - id    a stable random value (never changes) — the color seed used on
             this reviewer's local comments. Survives renames.
     - name  a self-set display name, defaulting to a friendly random one
             themed to redline (the editor's red pen / ink / margin motif).

   This is a local label, not a security claim. It is snapshotted onto comments
   so an agent can carry authorship into a revised plan; there is no shared
   author directory or cross-browser identity synchronization.

   Highlight color is a per-browser display preference: "auto" (the default)
   keeps the color derived from the id; picking a palette swatch recolors
   YOUR comments on THIS screen. It is not written onto comments, so other
   reviewers still see your id-derived color.
   ============================================================ */

import { ref, computed, watchEffect } from 'vue'
import { hashColor } from './utils'
import { useTheme } from './useTheme'

const ID_KEY = 'md-reader-author-id'
const NAME_KEY = 'md-reader-author-name'
const HIGHLIGHT_KEY = 'md-reader-highlight-color'

/** One preset highlight color, with light- and dark-theme renderings. The
 *  prose mark is a ~32% mix of this over the page, so these are saturated
 *  mid-tones; dark themes get a lighter step so marks stay legible. Red is
 *  left out on purpose — it is the brand's caret/mark color. */
export interface HighlightSwatch {
  id: string
  name: string
  light: string
  dark: string
}

export const HIGHLIGHT_PALETTE: readonly HighlightSwatch[] = [
  { id: 'yellow', name: 'Yellow', light: 'oklch(0.82 0.16 95)', dark: 'oklch(0.84 0.15 95)' },
  { id: 'green', name: 'Green', light: 'oklch(0.64 0.16 150)', dark: 'oklch(0.75 0.14 150)' },
  { id: 'teal', name: 'Teal', light: 'oklch(0.63 0.12 190)', dark: 'oklch(0.76 0.11 190)' },
  { id: 'blue', name: 'Blue', light: 'oklch(0.60 0.15 250)', dark: 'oklch(0.73 0.13 250)' },
  { id: 'violet', name: 'Violet', light: 'oklch(0.57 0.17 295)', dark: 'oklch(0.72 0.14 295)' },
  { id: 'pink', name: 'Pink', light: 'oklch(0.65 0.19 350)', dark: 'oklch(0.76 0.15 350)' },
]

/** The default choice: keep the color derived from the reviewer id. */
export const AUTO_HIGHLIGHT = 'auto'

// Redline theme: an ink/proofreading/margin adjective + a friendly creature.
// "Crimson Otter", "Margin Wren", "Redlined Lynx" — anonymous but on-brand.
const ADJECTIVES = [
  'Crimson', 'Inky', 'Scarlet', 'Penciled', 'Dotted', 'Ruled', 'Drafty',
  'Quilled', 'Bold', 'Italic', 'Cursive', 'Margin', 'Folio', 'Footnote',
  'Annotated', 'Scribbled', 'Underlined', 'Dog-eared', 'Redlined', 'Verso',
]
const CREATURES = [
  'Otter', 'Wombat', 'Heron', 'Lynx', 'Fox', 'Wren', 'Badger', 'Marten',
  'Finch', 'Tapir', 'Quokka', 'Vole', 'Stoat', 'Civet', 'Ibis', 'Gecko',
  'Pangolin', 'Lemur', 'Mongoose', 'Capybara',
]

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

/** A fresh themed display name, e.g. "Crimson Otter". */
export function randomDisplayName(): string {
  return `${pick(ADJECTIVES)} ${pick(CREATURES)}`
}

function readOrCreate(key: string, make: () => string): string {
  try {
    const existing = localStorage.getItem(key)
    if (existing) return existing
    const fresh = make()
    localStorage.setItem(key, fresh)
    return fresh
  } catch {
    // localStorage unavailable (private mode / blocked) — ephemeral identity.
    return make()
  }
}

function readHighlight(): string {
  try {
    const stored = localStorage.getItem(HIGHLIGHT_KEY)
    if (stored && HIGHLIGHT_PALETTE.some((p) => p.id === stored)) return stored
  } catch {
    /* storage unavailable — fall through to the default */
  }
  return AUTO_HIGHLIGHT
}

const id = ref(readOrCreate(ID_KEY, () => crypto.randomUUID()))
const name = ref(readOrCreate(NAME_KEY, randomDisplayName))
const highlight = ref(readHighlight())
// Read the theme id so colors recompute when the theme (and light/dark) flips.
const { themeId } = useTheme()

/** My current highlight color, resolved for the active theme. */
const color = computed(() => {
  void themeId.value
  const swatch = HIGHLIGHT_PALETTE.find((p) => p.id === highlight.value)
  if (!swatch) return hashColor(id.value)
  return document.documentElement.dataset.mode === 'dark' ? swatch.dark : swatch.light
})

/** The color for any comment author: my chosen color for my own comments,
 *  the id-derived color for everyone else. */
export function authorColor(authorId: string): string {
  return authorId === id.value ? color.value : hashColor(authorId)
}

// Mirror my color into a root CSS var so the live drag-selection (`.prose
// ::selection` in app.css) renders in my hue — the same color the committed
// mark will take, so selecting and marking read as one gesture.
watchEffect(() => {
  document.documentElement.style.setProperty('--my-author-color', color.value)
})

export function useIdentity() {
  /** Update the local display name. Empty input is ignored. */
  function setName(next: string): void {
    const trimmed = next.trim()
    if (!trimmed || trimmed === name.value) return
    name.value = trimmed
    try {
      localStorage.setItem(NAME_KEY, trimmed)
    } catch {
      /* ephemeral — keep the in-memory value */
    }
  }

  /** Pick a palette swatch id, or AUTO_HIGHLIGHT for the id-derived color. */
  function setHighlight(next: string): void {
    const valid = next === AUTO_HIGHLIGHT || HIGHLIGHT_PALETTE.some((p) => p.id === next)
    if (!valid || next === highlight.value) return
    highlight.value = next
    try {
      if (next === AUTO_HIGHLIGHT) localStorage.removeItem(HIGHLIGHT_KEY)
      else localStorage.setItem(HIGHLIGHT_KEY, next)
    } catch {
      /* ephemeral — keep the in-memory value */
    }
  }

  return {
    id,
    name,
    /** My highlight color: the chosen swatch, or the id-derived default. */
    color,
    /** The selected swatch id, or AUTO_HIGHLIGHT. */
    highlight: computed(() => highlight.value),
    /** Every choice, resolved for the active theme: Automatic first, then presets. */
    swatches: computed(() => {
      void themeId.value
      const dark = document.documentElement.dataset.mode === 'dark'
      return [
        { id: AUTO_HIGHLIGHT, name: 'Automatic', color: hashColor(id.value) },
        ...HIGHLIGHT_PALETTE.map((p) => ({ id: p.id, name: p.name, color: dark ? p.dark : p.light })),
      ]
    }),
    /** The `{ id, name }` snapshot to stamp onto a new comment. */
    author: computed(() => ({ id: id.value, name: name.value })),
    setName,
    setHighlight,
  }
}
